import { useGame } from '@/contexts/GameContext';
import { usePerformanceMonitor } from '@/hooks/usePerformanceMonitor';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

interface PerformanceMonitorProps {
  showDebugInfo?: boolean;
  autoOptimize?: boolean;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({
  showDebugInfo = false,
  autoOptimize = true,
}) => {
  const { updateGameState, gameState } = useGame();
  const optimizationAppliedRef = useRef(false);

  const { metrics, isPerformant } = usePerformanceMonitor({
    targetFPS: 120,
    enabled: true,
    onPerformanceIssue: metrics => {
      if (!autoOptimize || optimizationAppliedRef.current) return;

      // Auto-adjust graphics settings if FPS drops below 108 (90% of 120)
      if (metrics.fps < 108 && gameState.gameSettings.graphics !== 'low') {
        optimizationAppliedRef.current = true;

        if (gameState.gameSettings.graphics === 'high') {
          // First try medium graphics
          updateGameState({
            gameSettings: { ...gameState.gameSettings, graphics: 'medium' },
          });
        } else if (gameState.gameSettings.graphics === 'medium') {
          // Then try low graphics and disable effects
          updateGameState({
            gameSettings: {
              ...gameState.gameSettings,
              graphics: 'low',
              effects: false,
            },
          });
        }

        // Reset optimization flag after 10 seconds
        setTimeout(() => {
          optimizationAppliedRef.current = false;
        }, 10000);
      }
    },
  });

  // Reset optimization flag when returning to high performance
  useEffect(() => {
    if (isPerformant && optimizationAppliedRef.current) {
      setTimeout(() => {
        optimizationAppliedRef.current = false;
      }, 5000);
    }
  }, [isPerformant]);

  if (!showDebugInfo) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        style={{
          position: 'fixed',
          top: 10,
          left: 10,
          padding: '8px 12px',
          background: isPerformant ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
          border: `1px solid ${isPerformant ? '#00ff00' : '#ff0000'}`,
          borderRadius: 4,
          fontFamily: 'monospace',
          fontSize: 12,
          color: isPerformant ? '#00ff00' : '#ff0000',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        <div>FPS: {metrics.fps.toFixed(0)}/120</div>
        <div>Frame Time: {metrics.frameTime.toFixed(2)}ms</div>
        <div>Dropped: {metrics.droppedFrames}</div>
        <div>Graphics: {gameState.gameSettings.graphics}</div>
        <div>Effects: {gameState.gameSettings.effects ? 'ON' : 'OFF'}</div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PerformanceMonitor;
